import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Shield, Target, ListChecks, Trophy } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { getShowcase, getMeta } from "@/lib/api";

export default function SolvedCaseDetail() {
    const { caseId } = useParams();
    const [c, setC] = useState(null);
    const [meta, setMeta] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        getMeta().then(setMeta).catch(() => {});
        getShowcase(caseId)
            .then(setC)
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [caseId]);

    if (loading) {
        return (
            <div className="bg-[#0a0a0a] min-h-screen text-white">
                <Header />
                <div className="text-center py-32">
                    <Loader2 size={28} className="animate-spin mx-auto text-[#FF3B30]" />
                </div>
            </div>
        );
    }
    if (error || !c) {
        return (
            <div className="bg-[#0a0a0a] min-h-screen text-white">
                <Header />
                <div className="text-center py-32">
                    <div className="font-anton text-3xl uppercase">Not eligible for public showcase.</div>
                    <Link to="/hpm-cases/solved" className="btn-brutal mt-6 inline-flex">
                        ← BACK TO SOLVED CASES
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-[#0a0a0a] min-h-screen text-white">
            <Header />
            <div className="max-w-[1200px] mx-auto px-6 sm:px-10 py-10">
                <Link
                    to="/hpm-cases/solved"
                    className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 hover:text-white flex items-center gap-2 mb-6"
                >
                    <ArrowLeft size={14} /> ALL SOLVED CASES
                </Link>

                <div className="border-2 border-white/20 p-8 mb-8" data-testid="solved-detail-header">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
                        PUBLIC LEDGER · CASE #{(c.id || "").slice(0, 6).toUpperCase()} · ALL IDENTIFYING DETAILS REDACTED
                    </div>
                    <div className="flex flex-wrap justify-between items-end gap-6">
                        <div>
                            <h1 className="font-anton text-5xl sm:text-7xl uppercase leading-[0.85]">
                                {c.plaintiff_redacted}<br />
                                <span className="text-white/30 text-3xl">v.</span> {c.defendant_redacted}
                            </h1>
                            <div className="font-mono text-xs uppercase tracking-wider text-white/60 mt-4">
                                {(c.case_type || "").replace(/_/g, " ")} · {c.jurisdiction}
                                {c.state ? ` · ${c.state}` : ""} · {c.language}
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                                STRENGTH
                            </div>
                            <div className="font-anton text-8xl text-[#FF3B30] leading-none">
                                {c.strength_score}
                            </div>
                            {c.status === "matched" && (
                                <div className="mt-3 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#FFCC00] border-2 border-[#FFCC00] px-2 py-1">
                                    <Trophy size={10} /> ATTORNEY MATCHED
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {c.analysis_summary && (
                    <div className="border-2 border-white/20 p-6 mb-8">
                        <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50 mb-3">
                            STRATEGY SUMMARY
                        </div>
                        <p className="font-sans text-base text-white/85 leading-relaxed">
                            {c.analysis_summary}
                        </p>
                    </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border-2 border-white/20 mb-8">
                    {[
                        { title: "VIOLATIONS", items: c.violations_top3, icon: Shield },
                        { title: "PRECEDENTS", items: c.precedents_top3, icon: Target },
                        { title: "RECOMMENDED ACTIONS", items: c.actions_top3, icon: ListChecks },
                    ].map((sec, i) => (
                        <div
                            key={i}
                            className="p-6 border-white/20 md:[&:not(:last-child)]:border-r-2 [&:not(:last-child)]:border-b-2 md:[&:not(:last-child)]:border-b-0"
                        >
                            <div className="flex items-center gap-2 mb-4">
                                <sec.icon size={16} className="text-[#FF3B30]" />
                                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
                                    {sec.title}
                                </div>
                            </div>
                            <ul className="space-y-2">
                                {(sec.items || []).map((v, j) => (
                                    <li
                                        key={j}
                                        className="font-sans text-sm text-white/80 flex gap-2"
                                    >
                                        <span className="font-mono text-xs text-[#FF3B30]">
                                            {String(j + 1).padStart(2, "0")}
                                        </span>
                                        <span>{v}</span>
                                    </li>
                                ))}
                                {(!sec.items || sec.items.length === 0) && (
                                    <li className="font-mono text-xs uppercase tracking-wider text-white/30">
                                        — NONE LISTED —
                                    </li>
                                )}
                            </ul>
                        </div>
                    ))}
                </div>

                <div className="border-2 border-[#FF3B30] bg-[#FF3B30]/5 p-8 text-center" data-testid="solved-cta">
                    <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/70 mb-3">
                        YOUR CASE COULD BE NEXT
                    </div>
                    <h2 className="font-anton text-4xl uppercase mb-4">
                        Get yours scored in minutes.
                    </h2>
                    <Link to="/hpm-submit" className="btn-brutal btn-brutal-red">
                        START YOUR CASE →
                    </Link>
                </div>
            </div>
            <Footer disclaimer={meta?.disclaimer || ""} />
        </div>
    );
}
